import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNITS = ["g", "kg", "ml", "lt", "pz"];

export const LightRecipeEditorDialog = ({ recipe, localeId, getAuthHeaders, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [parts, setParts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!recipe) return;
    setName(recipe.nome);
    setParts((recipe.ingredienti || []).map((part, index) => ({
      indice_originale: index,
      nome: part.ingrediente_nome || "",
      quantita: String(part.quantita ?? ""),
      unita_misura: part.unita_misura || "g",
      linked: !!(part.ingrediente_id || part.ricetta_id),
    })));
  }, [recipe]);

  const changePart = (index, key, value) => {
    setParts(previous => previous.map((part, i) => i === index ? { ...part, [key]: value } : part));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!recipe) return;
    if (!name.trim() || !parts.length || parts.some(p =>
      !p.nome.trim() || !Number.isFinite(Number(p.quantita)) || Number(p.quantita) <= 0)) {
      toast.error("Indica un nome e una quantità positiva per ogni componente");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/ricette-light/${recipe.id}`, {
        locale_id: localeId, nome: name.trim(),
        original_nome: recipe.nome, original_ingredienti: recipe.ingredienti || [],
        componenti: parts.map(p => ({ indice_originale: p.indice_originale,
          nome: p.nome.trim(), quantita: Number(p.quantita), unita_misura: p.unita_misura })),
      }, { headers: getAuthHeaders() });
      const refreshed = await onSaved();
      onClose();
      if (refreshed === false) {
        toast.warning("Ricetta salvata. Riapri Prepara per aggiornare l'elenco.");
      } else {
        toast.success("Ricetta Light aggiornata. I lotti precedenti non sono cambiati.");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossibile salvare la ricetta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-5" data-testid="light-recipe-editor-dialog">
        <p className="text-sm text-muted-foreground">Le modifiche varranno solo per le prossime preparazioni.</p>
        <form onSubmit={save} className="space-y-5" data-testid="light-recipe-editor-form">
          <div className="space-y-2">
            <label htmlFor="light-edit-name" className="text-sm font-medium">Nome ricetta</label>
            <Input id="light-edit-name" value={name} onChange={e => setName(e.target.value)} maxLength={150} data-testid="light-edit-name" />
          </div>
          <div className="space-y-3">
            <p className="text-sm font-medium">Componenti</p>
            {parts.map((part, index) => (
              <div key={part.indice_originale ?? `new-${index}`} className="grid grid-cols-[minmax(0,1fr)_5.5rem_2.5rem] sm:grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_2.25rem] gap-1.5 items-center border-b border-border pb-3 sm:border-0 sm:pb-0" data-testid={`light-edit-part-${index}`}>
                <Input aria-label={`Nome componente ${index + 1}`} value={part.nome} maxLength={150}
                  disabled={part.linked} onChange={e => changePart(index, "nome", e.target.value)}
                  className="min-w-0 col-span-3 sm:col-span-1" data-testid={`light-edit-part-name-${index}`} />
                <Input aria-label={`Quantità componente ${index + 1}`} type="number" inputMode="decimal" step="any" min="0.001"
                  value={part.quantita} onChange={e => changePart(index, "quantita", e.target.value)}
                  className="min-w-0 px-2" data-testid={`light-edit-part-quantity-${index}`} />
                <Select value={part.unita_misura} onValueChange={value => changePart(index, "unita_misura", value)}>
                  <SelectTrigger aria-label={`Unità componente ${index + 1}`} className="min-w-0 px-2" data-testid={`light-edit-part-unit-${index}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{[...new Set([...UNITS, part.unita_misura])].map(unit => <SelectItem key={unit} value={unit}>{unit}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="icon" title={`Rimuovi ${part.nome || "componente"}`}
                  aria-label={`Rimuovi ${part.nome || "componente"}`} onClick={() => setParts(previous => previous.filter((_, i) => i !== index))}
                  data-testid={`light-edit-remove-part-${index}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" className="w-full" onClick={() => setParts(previous => [...previous, {
              indice_originale: null, nome: "", quantita: "", unita_misura: "g", linked: false,
            }])} data-testid="light-edit-add-part"><Plus className="h-4 w-4 mr-2" />Aggiungi componente</Button>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={saving} data-testid="light-edit-cancel">Annulla</Button>
            <Button type="submit" className="flex-1" disabled={saving || !parts.length} data-testid="light-edit-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva modifiche
            </Button>
          </div>
        </form>
    </section>
  );
};